import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, X, Sparkles, Compass, Lock } from "lucide-react";
import api from "../lib/api";
import BodyGraph from "./BodyGraph";
import { gateName, gateKeynote, ANGLE_MEANINGS, quarterOf } from "../lib/gates";
import { crossName, crossPurpose } from "../lib/crosses";

const CENTER_GATES = {
  Head: [64, 61, 63], Ajna: [47, 24, 4, 17, 43, 11],
  Throat: [62, 23, 56, 16, 20, 31, 8, 33, 35, 12, 45],
  G: [1, 13, 25, 46, 2, 15, 10, 7], Heart: [21, 40, 26, 51],
  Spleen: [48, 57, 44, 50, 32, 28, 18],
  Sacral: [34, 5, 14, 29, 59, 9, 3, 42, 27],
  "Solar Plexus": [6, 37, 22, 36, 30, 55, 49],
  Root: [58, 38, 54, 53, 60, 52, 19, 39, 41],
};

function GateChip({ gate, kind, active, onClick }) {
  // kind: 'both' | 'personality' | 'design' | null
  const cls = kind === "both"
    ? "bg-gradient-to-r from-foreground to-accent text-background border-transparent"
    : kind === "personality" ? "bg-foreground text-background border-transparent"
    : kind === "design" ? "bg-accent text-background border-transparent"
    : "border-border text-muted-foreground/50";
  return (
    <button data-testid={`gate-chip-${gate}`} disabled={!kind} onClick={() => kind && onClick(gate)}
      title={`Gate ${gate} · ${gateName(gate)}`}
      className={`h-8 w-8 rounded-md text-xs font-mono border transition-transform hover:scale-110 ${cls} ${active ? "ring-2 ring-primary" : ""}`}>
      {gate}
    </button>
  );
}

export default function HumanDesignChart({ hd, profileId }) {
  const [insight, setInsight] = useState(null); // {gate, text, loading, context}
  const cross = hd.incarnation_cross || {};
  const g = cross.gates || {};
  const angle = cross.angle || (cross.name || "").split(" Cross")[0].trim();
  const angleMeaning = ANGLE_MEANINGS[angle];
  const quarter = g.personality_sun ? quarterOf(g.personality_sun) : null;
  const traditionalName = g.personality_sun ? crossName(g.personality_sun, angle) : null;
  const displayTitle = traditionalName || cross.name || "—";
  const purpose = g.personality_sun ? crossPurpose(g.personality_sun, angle) : null;

  const pSet = new Set((hd.personality_gates || []).map((x) => x.gate));
  const dSet = new Set((hd.design_gates || []).map((x) => x.gate));
  const kindOf = (gate) => {
    const p = pSet.has(gate), d = dSet.has(gate);
    return p && d ? "both" : p ? "personality" : d ? "design" : null;
  };

  const openGate = async (gate) => {
    // show factual name + keynote instantly (works even with no AI budget)
    setInsight({ gate, name: gateName(gate), keynote: gateKeynote(gate), loading: true });
    try {
      const { data } = await api.get(`/profiles/${profileId}/gate/${gate}`);
      setInsight({
        gate,
        name: data.name || gateName(gate),
        keynote: data.keynote || gateKeynote(gate),
        context: data.context,
        text: data.text,
        aiAvailable: data.ai_available,
      });
    } catch {
      // still keep the factual card open even if the request fails
      setInsight({ gate, name: gateName(gate), keynote: gateKeynote(gate), aiAvailable: false });
      toast.error("Couldn't reach your companion — showing the essentials");
    }
  };

  return (
    <div className="panel p-6 md:p-8 mb-4" data-testid="hd-chart">
      {/* Incarnation Cross header */}
      <div className="mb-8" data-testid="incarnation-cross">
        <p className="overline mb-2 flex items-center gap-2"><Compass size={14} /> Incarnation Cross · your life purpose</p>
        <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-1.5" data-testid="cross-title">{displayTitle}</h2>
        {traditionalName && cross.name && (
          <p className="font-mono text-xs text-muted-foreground mb-4" data-testid="cross-technical-name">{cross.name}</p>
        )}
        {!(traditionalName && cross.name) && <div className="mb-4" />}
        {purpose && (
          <div className="rounded-xl border border-accent/30 bg-accent/[0.04] p-4 mb-2" data-testid="cross-why">
            <div className="overline mb-1.5 text-accent/90">Why you're here</div>
            <p className="text-sm text-foreground/85 leading-relaxed">{purpose}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {[["Personality Sun", g.personality_sun], ["Personality Earth", g.personality_earth],
            ["Design Sun", g.design_sun], ["Design Earth", g.design_earth]].map(([label, gate]) => (
            <button key={label} onClick={() => gate && openGate(gate)} data-testid={`cross-gate-${label.replace(/\s/g, "-").toLowerCase()}`}
              className="text-left px-4 py-2 rounded-xl border border-border hover:border-primary/50 transition-colors max-w-[200px]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className="font-serif text-2xl text-primary leading-tight">Gate {gate ?? "—"}</div>
              {gate && <div className="text-xs text-foreground/70 mt-0.5">{gateName(gate)}</div>}
            </button>
          ))}
        </div>

        {/* Life purpose: angle, quarter, and plain-language synthesis */}
        {(angleMeaning || quarter || g.personality_sun) && (
          <div className="mt-6 space-y-4" data-testid="cross-purpose">
            <div className="grid sm:grid-cols-2 gap-3">
              {angleMeaning && (
                <div className="rounded-xl border border-border p-4" data-testid="cross-angle">
                  <div className="overline mb-1.5">{angle}</div>
                  <p className="text-sm text-foreground/80 leading-relaxed">{angleMeaning}</p>
                </div>
              )}
              {quarter && (
                <div className="rounded-xl border border-border p-4" data-testid="cross-quarter">
                  <div className="overline mb-1.5">{quarter.name}</div>
                  <p className="text-sm text-foreground/80 leading-relaxed">Your {quarter.theme}.</p>
                </div>
              )}
            </div>
            {g.personality_sun && g.design_sun && (
              <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-4" data-testid="cross-synthesis">
                <div className="overline mb-1.5 text-primary/90">What this means for you</div>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  Your conscious direction is led by <span className="text-primary">{gateName(g.personality_sun)}</span> ({gateKeynote(g.personality_sun).replace(/\.$/, "").toLowerCase()}), anchored by <span className="text-primary">{gateName(g.personality_earth)}</span>. Beneath the surface, your Design carries <span className="text-accent">{gateName(g.design_sun)}</span> and <span className="text-accent">{gateName(g.design_earth)}</span> — the energy you were built with. Together these four gates form the theme you're here to live out{quarter ? ` — your ${quarter.name}, ${quarter.theme.split(" — ")[0]}` : ""}.
                </p>
                <p className="text-[11px] text-muted-foreground mt-2">Tap any gate above to explore it in depth.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {insight && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          data-testid="gate-insight-card" className="panel p-5 mb-6 border-primary/40">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-2">
                <Sparkles size={12} /> Gate {insight.gate} · {insight.name}
              </p>
              {insight.keynote && (
                <p className="font-serif text-lg text-foreground leading-snug mb-3" data-testid="gate-keynote">
                  {insight.keynote}
                </p>
              )}
              {insight.context && (
                <p className="text-sm text-foreground/80 leading-relaxed mb-3" data-testid="gate-context">
                  {insight.context}
                </p>
              )}
              {insight.loading ? (
                <div className="flex items-center gap-2 text-primary text-xs">
                  <Loader2 size={14} className="animate-spin" /> Reading this gate for you…
                </div>
              ) : insight.text ? (
                <div className="pt-3 border-t border-border/60">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                    Your personalized reading
                  </p>
                  <p className="text-sm text-foreground/90 leading-relaxed" data-testid="gate-ai-text">{insight.text}</p>
                </div>
              ) : insight.aiAvailable === false ? (
                <div className="pt-3 border-t border-border/60 flex items-start gap-2 text-xs text-muted-foreground" data-testid="gate-ai-unavailable">
                  <Lock size={13} className="mt-0.5 shrink-0 text-accent" />
                  <span>Top up your AI key to unlock a deeper, personalized reading of this gate — the essentials above are always here.</span>
                </div>
              ) : null}
            </div>
            <button onClick={() => setInsight(null)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
          </div>
        </motion.div>
      )}

      <div className="grid md:grid-cols-[220px_1fr] gap-8 items-start">
        <BodyGraph defined={hd.defined_centers} channels={hd.defined_channels} />
        <div>
          <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-foreground inline-block" /> Personality</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-accent inline-block" /> Design</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-gradient-to-r from-foreground to-accent inline-block" /> Both</span>
            <span>· tap any active gate</span>
          </div>
          <div className="space-y-3">
            {Object.entries(CENTER_GATES).map(([center, gates]) => {
              const activeGates = gates.filter((gt) => kindOf(gt));
              if (!activeGates.length) return null;
              const defined = (hd.defined_centers || []).includes(center);
              return (
                <div key={center} className="flex items-center gap-3">
                  <span className={`text-xs w-24 shrink-0 ${defined ? "text-primary" : "text-muted-foreground"}`}>{center}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {activeGates.map((gt) => (
                      <GateChip key={gt} gate={gt} kind={kindOf(gt)} onClick={openGate} active={insight?.gate === gt} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
